import React from "react";
import {
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  Link as MuiLink,
} from "@mui/material";
import { Facebook, Instagram, Twitter, LinkedIn } from "@mui/icons-material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";

const Footer = () => {
  return (
    <Box sx={{ bgcolor: "#0A1B39", color: "#94A3B8", pt: 8, pb: 4 }}>
      <Container maxWidth="xl">
        <Grid container spacing={5}>
          {/* Col 1: Brand & Emergency Disclaimer */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  bgcolor: "#135BEC",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#FFFFFF",
                }}
              >
                <LocalHospitalIcon sx={{ fontSize: 22 }} />
              </Box>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 800,
                  color: "#FFFFFF",
                  letterSpacing: "-0.5px",
                }}
              >
                Telemedicine
              </Typography>
            </Box>
            <Typography sx={{ fontSize: "14px", color: "#94A3B8", lineHeight: 1.6, mb: 2 }}>
              Your Health, Anywhere. Delivering certified virtual consultations,
              NIN-verified healthcare specialists, and seamless wallet payments across Nigeria.
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: "10px",
                bgcolor: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.25)",
              }}
            >
              <Typography sx={{ fontSize: "12px", color: "#FCA5A5", lineHeight: 1.5 }}>
                ⚠️ <strong>Medical Notice:</strong> If you are experiencing a life-threatening medical
                emergency, please dial local emergency services or visit the nearest clinic immediately.
              </Typography>
            </Box>
          </Grid>

          {/* Col 2: Quick Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "15px", mb: 2.5 }}>
              SERVICES
            </Typography>
            {["General Medicine", "Pediatrics", "Mental Health", "Dermatology", "Digital Prescriptions", "Health Wallet"].map((item, idx) => (
              <Typography key={idx} sx={{ fontSize: "14px", mb: 1.5, color: "#94A3B8", cursor: "pointer", "&:hover": { color: "#60A5FA" } }}>
                {item}
              </Typography>
            ))}
          </Grid>

          {/* Col 3: Legal & Governance */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "15px", mb: 2.5 }}>
              LEGAL
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <MuiLink href="/privacy-policy" underline="hover" sx={{ fontSize: "14px", color: "#94A3B8", "&:hover": { color: "#60A5FA" } }}>
                Privacy Policy
              </MuiLink>
              <MuiLink href="/terms-and-conditions" underline="hover" sx={{ fontSize: "14px", color: "#94A3B8", "&:hover": { color: "#60A5FA" } }}>
                Terms & Conditions
              </MuiLink>
              <Typography sx={{ fontSize: "14px", color: "#94A3B8", cursor: "pointer", "&:hover": { color: "#60A5FA" } }}>
                Refund Policy
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "#94A3B8", cursor: "pointer", "&:hover": { color: "#60A5FA" } }}>
                Doctor Verification (NIN)
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "#94A3B8", cursor: "pointer", "&:hover": { color: "#60A5FA" } }}>
                Patient Rights
              </Typography>
            </Box>
          </Grid>

          {/* Col 4: Newsletter & Contact */}
          <Grid item xs={12} sm={4} md={4}>
            <Typography sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "15px", mb: 2 }}>
              HEALTH NEWS & UPDATES
            </Typography>
            <Typography sx={{ fontSize: "14px", mb: 2, color: "#94A3B8" }}>
              Get verified healthcare tips and announcements delivered to your inbox weekly.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Your email address"
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.08)",
                  borderRadius: "8px",
                  flexGrow: 1,
                  input: { color: "#FFFFFF", fontSize: "14px" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.15)" },
                    "&:hover fieldset": { borderColor: "#135BEC" },
                  },
                }}
              />
              <Button
                variant="contained"
                sx={{
                  bgcolor: "#135BEC",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: "13px",
                  borderRadius: "8px",
                  textTransform: "none",
                  px: 2.5,
                  "&:hover": { bgcolor: "#0D4BCA" },
                }}
              >
                Join
              </Button>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton sx={{ color: "#94A3B8", "&:hover": { color: "#60A5FA" } }}>
                <Facebook />
              </IconButton>
              <IconButton sx={{ color: "#94A3B8", "&:hover": { color: "#60A5FA" } }}>
                <Instagram />
              </IconButton>
              <IconButton sx={{ color: "#94A3B8", "&:hover": { color: "#60A5FA" } }}>
                <Twitter />
              </IconButton>
              <IconButton sx={{ color: "#94A3B8", "&:hover": { color: "#60A5FA" } }}>
                <LinkedIn />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Box
          sx={{
            mt: 6,
            pt: 3,
            borderTop: "1px solid rgba(255, 255, 255, 0.08)",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography sx={{ fontSize: "13.5px", color: "#64748B" }}>
            © {new Date().getFullYear()} Telemedicine Technologies. All rights reserved.
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#64748B" }}>
            Licensed & Compliant Healthcare Infrastructure
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
